import { useState, useMemo, useEffect, useRef } from 'react';
import { DataTable, DataTableExpandedRows, DataTableFilterMeta, DataTableFilterEvent, DataTableSortEvent } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Message } from 'primereact/message';
import { Panel } from 'primereact/panel';
import { FilterMatchMode } from 'primereact/api';
import { CustomerDeposit, CustomerAllocation } from '../../types';
import { allocationsApi } from '../../api';
import { usePersistentState } from '../../hooks';
import { formatCurrency, formatDate, amountFilterFunction } from '../../utils';

interface UnallocatedDepositsPanelProps {
  clientId: string;
  deposits: CustomerDeposit[];
  loading: boolean;
  selectionMode?: 'multiple' | null;
  selectedDepositIds?: Set<string>;
  onSelectionChange?: (depositIds: Set<string>) => void;
  selectedLedgerEntryDate?: string | null;
  externalAmountFilter?: string | null;
}

export default function UnallocatedDepositsPanel({
  clientId,
  deposits,
  loading,
  selectionMode,
  selectedDepositIds,
  onSelectionChange,
  selectedLedgerEntryDate,
  externalAmountFilter,
}: UnallocatedDepositsPanelProps) {
  const [error] = useState<string | null>(null);
  const tableRef = useRef<any>(null);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(25);
  const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows>();
  const [customerAllocationsMap, setCustomerAllocationsMap] = useState<Map<string, CustomerAllocation[]>>(new Map());
  const [loadingAllocations, setLoadingAllocations] = useState(false);
  const defaultFilters: DataTableFilterMeta = {
    deposit_date: { value: null, matchMode: FilterMatchMode.CONTAINS },
    amount: { value: null, matchMode: 'custom_amount' as any },
    customer_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
  };
  const [storedFilters, setFilters] = usePersistentState<DataTableFilterMeta>(
    'reconciliation:unallocatedDeposits:filters',
    defaultFilters
  );
  // Ensure matchMode from defaults is always used (handles localStorage with old matchMode)
  const filters = useMemo(() => {
    return Object.keys(defaultFilters).reduce((acc, key) => {
      const defaultFilter = defaultFilters[key];
      const storedFilter = storedFilters[key];
      if (defaultFilter && typeof defaultFilter === 'object' && 'matchMode' in defaultFilter) {
        acc[key] = {
          ...defaultFilter,
          value: storedFilter && 'value' in storedFilter ? storedFilter.value : defaultFilter.value,
        };
      } else {
        acc[key] = storedFilter ?? defaultFilter;
      }
      return acc;
    }, {} as DataTableFilterMeta);
  }, [storedFilters]);
  const [sortState, setSortState] = usePersistentState<{
    sortField: string;
    sortOrder: 1 | 0 | -1 | null | undefined;
  }>('reconciliation:unallocatedDeposits:sort', {
    sortField: 'deposit_date',
    sortOrder: 1,
  });

  // Apply external amount filter when it changes
  useEffect(() => {
    if (externalAmountFilter !== undefined) {
      setFilters((prev) => ({
        ...prev,
        amount: { value: externalAmountFilter, matchMode: 'custom_amount' as any },
      }));
      // Reset to first page when filter is applied
      if (externalAmountFilter) {
        setFirst(0);
      }
    }
  }, [externalAmountFilter]);

  const dateTemplate = (rowData: CustomerDeposit) => {
    return formatDate(rowData.deposit_date);
  };

  const amountTemplate = (rowData: CustomerDeposit) => {
    return formatCurrency(rowData.amount);
  };

  const customerNameTemplate = (rowData: CustomerDeposit) => {
    return rowData.customer_name || rowData.raw_customer_name || '-';
  };

  // Helper to check if any filters are active
  const hasActiveFilters = useMemo(() => {
    const result = Object.values(filters).some(filter => {
      if ('value' in filter) {
        return filter.value !== null && filter.value !== '';
      }
      return false;
    });
    return result;
  }, [filters]);

  // Load allocations for a customer when row is expanded
  const handleRowToggle = async (e: { data: DataTableExpandedRows }) => {
    setExpandedRows(e.data);

    // Get newly expanded rows
    const newExpandedRows = e.data;
    if (newExpandedRows && typeof newExpandedRows === 'object') {
      for (const key of Object.keys(newExpandedRows)) {
        const deposit = deposits.find(d => d.id === key);
        if (deposit && deposit.customer_id && !customerAllocationsMap.has(deposit.customer_id)) {
          // Load allocations for this customer
          setLoadingAllocations(true);
          try {
            const allocations = await allocationsApi.getCustomerAllocations(clientId, deposit.customer_id);
            setCustomerAllocationsMap(prev => new Map(prev).set(deposit.customer_id!, allocations));
          } catch (err) {
            console.error('Failed to load customer allocations:', err);
          } finally {
            setLoadingAllocations(false);
          }
        }
      }
    }
  };

  const rowExpansionTemplate = (deposit: CustomerDeposit) => {
    if (!deposit.customer_id) {
      return (
        <div className="row-expansion-container">
          <Message severity="info" text="No customer linked to this deposit" />
        </div>
      );
    }

    const allocations = customerAllocationsMap.get(deposit.customer_id) || [];

    if (loadingAllocations && allocations.length === 0) {
      return (
        <div className="row-expansion-container">
          <Message severity="info" text="Loading allocations..." />
        </div>
      );
    }

    if (allocations.length === 0) {
      return (
        <div className="row-expansion-container">
          <Message severity="info" text="No allocations found for this customer" />
        </div>
      );
    }

    return (
      <div className="row-expansion-container">
        <Panel header="Customer Allocations">
          <DataTable value={allocations} size="small">
            <Column
              field="created_at"
              header="Date"
              style={{ width: '15%' }}
              body={(rowData: CustomerAllocation) => formatDate(rowData.created_at)}
            />
            <Column
              field="allocation_type"
              header="Type"
              style={{ width: '15%' }}
            />
            <Column
              field="amount"
              header="Amount"
              style={{ width: '18%' }}
              body={(rowData: CustomerAllocation) => formatCurrency(rowData.amount)}
            />
          </DataTable>
        </Panel>
      </div>
    );
  };

  const getSelectedDeposits = () => {
    if (selectionMode === 'multiple' && selectedDepositIds) {
      return deposits.filter((d) => d.id && selectedDepositIds.has(d.id));
    }
    return [];
  };

  const handleSelectionChange = (e: any) => {
    if (selectionMode === 'multiple' && onSelectionChange) {
      const selectedDeposits = e.value as CustomerDeposit[];
      const newSet = new Set<string>();
      selectedDeposits.forEach((d) => {
        if (d.id) newSet.add(d.id);
      });
      onSelectionChange(newSet);
    }
  };

  const dateRange = useMemo(() => {
    const validDates = deposits
      .map((d) => d.deposit_date)
      .filter((date): date is string => date !== null && date !== undefined)
      .map((date) => new Date(date))
      .filter((date) => !isNaN(date.getTime()));

    if (validDates.length === 0) return null;

    const minDate = new Date(Math.min(...validDates.map((d) => d.getTime())));
    const maxDate = new Date(Math.max(...validDates.map((d) => d.getTime())));

    return {
      min: minDate.toLocaleDateString(),
      max: maxDate.toLocaleDateString(),
    };
  }, [deposits]);

  // Normalize date to just the date string (YYYY-MM-DD) to avoid timezone issues
  const normalizeDateString = (dateStr: string): string => {
    return dateStr.split('T')[0]; // Get just the YYYY-MM-DD part
  };

  // Sort deposits by date (same as table sorting)
  const sortedDeposits = useMemo(() => {
    return [...deposits].sort((a, b) => {
      if (!a.deposit_date) return 1;
      if (!b.deposit_date) return -1;
      return a.deposit_date.localeCompare(b.deposit_date);
    });
  }, [deposits]);

  // Find deposits matching or closest to the selected ledger entry date
  const { matchingDepositIndex, matchingDepositId, isExactMatch } = useMemo(() => {
    if (!selectedLedgerEntryDate || sortedDeposits.length === 0) {
      return { matchingDepositIndex: -1, matchingDepositId: null, isExactMatch: false };
    }

    const normalizedEntryDate = normalizeDateString(selectedLedgerEntryDate);

    // First try to find exact match
    const exactMatchIndex = sortedDeposits.findIndex((d) => {
      if (!d.deposit_date) return false;
      const normalizedDepositDate = normalizeDateString(d.deposit_date);
      return normalizedDepositDate === normalizedEntryDate;
    });

    if (exactMatchIndex >= 0) {
      return {
        matchingDepositIndex: exactMatchIndex,
        matchingDepositId: sortedDeposits[exactMatchIndex].id,
        isExactMatch: true,
      };
    }

    // No exact match - find closest date
    let closestIndex = -1;
    let smallestDiff = Infinity;
    const entryTime = new Date(normalizedEntryDate).getTime();

    sortedDeposits.forEach((d, idx) => {
      if (!d.deposit_date) return;
      const depositTime = new Date(normalizeDateString(d.deposit_date)).getTime();
      const diff = Math.abs(depositTime - entryTime);

      if (diff < smallestDiff) {
        smallestDiff = diff;
        closestIndex = idx;
      }
    });

    return {
      matchingDepositIndex: closestIndex,
      matchingDepositId: closestIndex >= 0 ? sortedDeposits[closestIndex].id : null,
      isExactMatch: false,
    };
  }, [selectedLedgerEntryDate, sortedDeposits]);

  // Scroll to matching deposit when ledger entry is selected
  useEffect(() => {
    // Don't change pages when filters are active - the matchingDepositIndex is based on
    // the unfiltered array, but DataTable is showing filtered results
    if (hasActiveFilters) {
      return;
    }

    if (matchingDepositIndex >= 0) {
      // Calculate which page the matching deposit is on
      const pageIndex = Math.floor(matchingDepositIndex / rows);
      const targetFirst = pageIndex * rows;

      if (targetFirst !== first) {
        setFirst(targetFirst);
      } else {
        // If we're already on the correct page, scroll immediately
        scrollToHighlightedRow();
      }
    }
  }, [matchingDepositIndex, rows, first, hasActiveFilters]);

  // Scroll to the highlighted row after page change or selection change
  useEffect(() => {
    // Don't scroll when filters are active
    if (hasActiveFilters) {
      return;
    }

    if (matchingDepositIndex >= 0 && first >= 0 && selectedLedgerEntryDate) {
      // Small delay to ensure DataTable has rendered the new page/selection
      const timer = setTimeout(() => {
        scrollToHighlightedRow();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [first, matchingDepositIndex, selectedLedgerEntryDate, hasActiveFilters]);

  const scrollToHighlightedRow = () => {
    // Don't scroll to highlighted row if filters are active
    if (hasActiveFilters) {
      return;
    }

    if (!tableRef.current) return;

    // Find the highlighted row element
    const tableElement = (tableRef.current as any).getElement();
    if (!tableElement) return;

    const highlightedRow = tableElement.querySelector('.highlight-row');
    if (!highlightedRow) return;

    // Find the scrollable body container (created by scrollable prop)
    const scrollBody = tableElement.querySelector('.p-datatable-scrollable-body');
    if (scrollBody) {
      // Calculate the position of the highlighted row relative to the scroll container
      const rowRect = highlightedRow.getBoundingClientRect();
      const scrollRect = scrollBody.getBoundingClientRect();
      const scrollTop = scrollBody.scrollTop;

      // Position the row 1/2 from the top of the visible area for better visibility
      const offset = scrollRect.height / 2;
      const targetScroll = scrollTop + (rowRect.top - scrollRect.top) - offset;

      scrollBody.scrollTo({ top: Math.max(0, targetScroll), behavior: 'smooth' });
    } else {
      // Fallback to regular scrollIntoView if not using scrollable mode
      highlightedRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleFilter = (event: DataTableFilterEvent) => {
    setFilters(event.filters);
  };

  const handleSort = (event: DataTableSortEvent) => {
    setSortState({
      sortField: event.sortField ?? sortState.sortField,
      sortOrder: event.sortOrder ?? sortState.sortOrder,
    });
  };

  return (
    <>
      {error && <Message severity="error" text={error} />}

      {deposits.length === 0 && !loading ? (
        <Message
          severity="info"
          text="No unallocated deposits found. All deposits have been assigned to ledger entries."
        />
      ) : (
        <>
          <h3>
            Customer Deposits
            {dateRange && (
              <span className="date-range">
                ({dateRange.min} - {dateRange.max})
              </span>
            )}
          </h3>
          <DataTable
            ref={tableRef}
            value={deposits}
            dataKey="id"
            className="highlight-table"
            size="small"
            scrollable
            scrollHeight="flex"
            paginator
            first={first}
            rows={rows}
            selectionMode="checkbox"
            onPage={(e) => {
              setFirst(e.first);
              setRows(e.rows);
            }}
            sortField={sortState.sortField}
            sortOrder={sortState.sortOrder ?? undefined}
            rowsPerPageOptions={[25, 50, 100]}
            paginatorTemplate={{
              layout:
                'RowsPerPageDropdown FirstPageLink PrevPageLink NextPageLink LastPageLink CurrentPageReport',
              CurrentPageReport: (options) => (
                <span className="paginator-current">
                  Showing {options.first}-{options.last} of <b>{options.totalRecords}</b> deposits
                </span>
              ),
            }}
            loading={loading}
            filters={filters}
            onFilter={handleFilter}
            filterDisplay="menu"
            onSort={handleSort}
            stripedRows
            rowClassName={(data) => {
              // Check if this row is the matching deposit
              if (matchingDepositId && data.id === matchingDepositId) {
                // If it's an exact match, use primary highlight
                if (isExactMatch) {
                  return 'highlight-row';
                }
                // If it's the closest match (not exact), use orange highlight
                return 'highlight-row highlight-row-closest';
              }

              return '';
            }}
            selection={getSelectedDeposits()}
            onSelectionChange={handleSelectionChange}
            expandedRows={expandedRows}
            onRowToggle={handleRowToggle}
            rowExpansionTemplate={rowExpansionTemplate}
          >
            <Column expander style={{ width: '3%' }} />
            <Column selectionMode="multiple" style={{ width: '3%' }} />
            <Column
              field="deposit_date"
              header="Date"
              style={{ width: '20%' }}
              sortable
              body={dateTemplate}
              filter
              filterPlaceholder="Search by date"
              showFilterMatchModes={false}
            />
            <Column
              field="amount"
              header="Amount"
              style={{ width: '20%' }}
              sortable
              body={amountTemplate}
              filter
              filterPlaceholder="Search by amount"
              showFilterMatchModes={false}
              filterFunction={amountFilterFunction}
            />
            <Column
              field="customer_name"
              header="Customer"
              sortable
              body={customerNameTemplate}
              filter
              filterPlaceholder="Search by customer"
              showFilterMatchModes={false}
            />
          </DataTable>
        </>
      )}
    </>
  );
}
